import {
    Box,
    Combobox,
    ComboboxInput,
    ComboboxList,
    ComboboxOption,
    ComboboxPopover,
    FlexBox,
    FormField,
    FormLabel,
    H2,
    Link,
    Spinner,
    ToggleSwitch,
    Typography,
} from '@vp/swan';
import { __ } from '@99designs/i18n';
import { ANY_DESIGNER_CALENDAR_ID } from './appointmentValue';

export interface PreviousDesigner {
    acuityCalendarId: number;
    designerName: string;
}

interface DesignerSelectorProps {
    id: string;
    isSignedIn: boolean;
    onSignIn: () => void;
    matchWithPreviousDesigner: boolean;
    onToggleChange: (activated: boolean) => void;
    designers: PreviousDesigner[];
    isLoadingDesigners: boolean;
    hasDesignersError: boolean;
    calendarId: number;
    setCalendarId: (calendarId: number) => void;
    // Locks booked appointments.
    immutable?: boolean | null;
}

// Renders designer selection controls for the multi-step brief form. The chat brief has
// its own dedicated ChatDesignerSelector (in feature-chat-brief) rather than sharing this
// component — the two surfaces render this section differently enough (heading, label
// weight, spacing) that a single component with a mode flag was harder to follow than two
// small components.
export default function DesignerSelector({
    id,
    isSignedIn,
    onSignIn,
    matchWithPreviousDesigner,
    onToggleChange,
    designers,
    isLoadingDesigners,
    hasDesignersError,
    calendarId,
    setCalendarId,
    immutable,
}: DesignerSelectorProps) {
    const isDisabled = !!immutable;
    const toggleLabel = __("Match me with a designer I've worked with before");

    return (
        <Box marginBottom="7" data-testid={`${id}-section`}>
            <H2 fontSkin="title-subsection" mb={4}>
                {__('Designer selection', {
                    _context:
                        'Heading for the section where a customer chooses whether to book a DesignLive session with a designer they have worked with before',
                })}
            </H2>
            <FormField>
                <FlexBox alignItems="center" justifyContent="space-between" gap="between-actions">
                    <FormLabel fontSkin="body-standard" htmlFor={id} mb={0}>
                        {toggleLabel}
                    </FormLabel>
                    <ToggleSwitch
                        id={id}
                        hideText
                        standardMode
                        activated={matchWithPreviousDesigner}
                        aria-label={toggleLabel}
                        disabled={isDisabled}
                        onRequestActivatedChange={(activated, e) => {
                            e.preventDefault();
                            onToggleChange(activated);
                        }}
                    />
                </FlexBox>
            </FormField>

            {matchWithPreviousDesigner && (
                <DesignerMatchContent
                    id={id}
                    isSignedIn={isSignedIn}
                    onSignIn={onSignIn}
                    designers={designers}
                    isLoadingDesigners={isLoadingDesigners}
                    hasDesignersError={hasDesignersError}
                    calendarId={calendarId}
                    setCalendarId={setCalendarId}
                    isDisabled={isDisabled}
                />
            )}
        </Box>
    );
}

interface DesignerMatchContentProps {
    id: string;
    isSignedIn: boolean;
    onSignIn: () => void;
    designers: PreviousDesigner[];
    isLoadingDesigners: boolean;
    hasDesignersError: boolean;
    calendarId: number;
    setCalendarId: (calendarId: number) => void;
    isDisabled: boolean;
}

// Renders the state below the toggle once designer matching is enabled.
// The states are mutually exclusive and evaluated top to bottom.
function DesignerMatchContent({
    id,
    isSignedIn,
    onSignIn,
    designers,
    isLoadingDesigners,
    hasDesignersError,
    calendarId,
    setCalendarId,
    isDisabled,
}: DesignerMatchContentProps) {
    if (!isSignedIn) {
        return (
            <Box mt={3}>
                <Link
                    skin="standard"
                    render={({ children, ...props }) => (
                        <button type="button" {...props} onClick={onSignIn}>
                            {children}
                        </button>
                    )}
                >
                    {__('Sign in to select from your previous designers', {
                        _context:
                            'Link prompting a signed-out customer to sign in so they can pick a designer they have worked with before',
                    })}
                    {' \u2192'}
                </Link>
            </Box>
        );
    }

    if (isLoadingDesigners) {
        return (
            <Box mt={4} textAlign="center" data-testid={`${id}-loading`}>
                <Spinner accessibleText={__('Loading your previous designers...')} />
            </Box>
        );
    }

    if (designers.length > 0) {
        return (
            <Box mt={4}>
                <FormField>
                    <FormLabel
                        id={`${id}-dropdown-label`}
                        fontSkin="body-standard-bold"
                        marginBottom={'3'}
                    >
                        {__('Choose from your past designers', {
                            _context:
                                'Label for a dropdown to pick which previously-worked-with designer to book a DesignLive session with',
                        })}
                    </FormLabel>
                    <Combobox
                        selectedKey={String(calendarId)}
                        onSelectionChange={(key) => {
                            if (key !== null) setCalendarId(Number(key));
                        }}
                        menuTrigger="focus"
                    >
                        <ComboboxInput
                            data-testid={`${id}-dropdown`}
                            placeholder={__('Select a designer')}
                            disabled={isDisabled}
                            aria-labelledby={`${id}-dropdown-label`}
                        />
                        <ComboboxPopover>
                            <ComboboxList data-testid={`${id}-dropdown-list`}>
                                {designers.map((designer) => (
                                    <ComboboxOption
                                        key={designer.acuityCalendarId}
                                        textValue={designer.designerName}
                                    >
                                        {designer.designerName}
                                    </ComboboxOption>
                                ))}
                            </ComboboxList>
                        </ComboboxPopover>
                    </Combobox>
                </FormField>
                <Typography fontSkin="body-small" textColor="subtle" mt={2}>
                    {__("Selecting a designer will only show times when they're available", {
                        _context:
                            'Helper text explaining that picking a designer filters the available appointment times',
                    })}
                </Typography>
            </Box>
        );
    }

    if (hasDesignersError) {
        // Only claim an any-designer fallback when no specific designer is still selected.
        if (calendarId !== ANY_DESIGNER_CALENDAR_ID) {
            return null;
        }
        return (
            <Typography fontSkin="body-small" textColor="subtle" mt={3}>
                {__(
                    "We couldn't load your previous designers. We'll match you with a designer that suits your selected time.",
                    {
                        _context:
                            'Helper text shown when loading the customer\u2019s previous designers failed',
                    }
                )}
            </Typography>
        );
    }

    return (
        <Typography fontSkin="body-small" textColor="subtle" mt={3}>
            {__(
                "You haven't worked with a designer before, so we'll match you with a designer that suits your selected time.",
                {
                    _context:
                        'Helper text shown when a customer has opted in to designer matching but has no previous designers',
                }
            )}
        </Typography>
    );
}
